import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { Shield, Eye, Lock } from "lucide-react";

/**
 * First-run workplace-surveillance disclosure modal.
 *
 * Compliance backbone for the SaaS rollout:
 *   - GDPR Art. 13 (right-to-be-informed at data collection)
 *   - LGPD Art. 9 (Brazilian equivalent)
 *   - California Workplace Surveillance Notice rules
 *   - Loose alignment with the EU AI Act's "deployer" disclosure
 *     requirements when AI is summarising employees' work
 *
 * The modal fires automatically when `userPreferences.disclosureAckedAt`
 * is null. Acking writes the timestamp + an audit row. The modal
 * cannot be dismissed without clicking "I understand" — this is
 * the compliance signal we'll point to if a regulator ever asks
 * "how did you obtain informed consent."
 *
 * Localised EN/AR. The wording is deliberately plain — buzzwords
 * undermine the "informed" part of informed consent.
 */
export function DisclosureModal({
  open,
  onAck,
}: {
  open: boolean;
  onAck: () => void;
}) {
  const { locale } = useI18n();
  const ack = trpc.preferences.ackDisclosure.useMutation({
    onSuccess: () => onAck(),
  });
  const ar = locale === "ar";

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        // Block both Escape and click-outside — the modal is consent,
        // not a tip. The only exit is the explicit "I understand" button.
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        className="sm:max-w-lg max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-none max-sm:max-h-[90dvh] max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto"
      >
        <DialogTitle className="font-display text-xl flex items-center gap-2">
          <Shield className="h-5 w-5 text-coral" />
          {ar ? "كيف تتعامل ريتايف مع بياناتك" : "How Retayf handles your data"}
        </DialogTitle>

        <div className="space-y-4 mt-2 text-sm leading-relaxed">
          <p className="text-foreground/85">
            {ar
              ? "ريتايف يجمع نشاطك في العمل ويلخّصه أسبوعياً بمساعدة الذكاء الاصطناعي. قبل البدء، إليك ما تحتاج معرفته:"
              : "Retayf collects your work activity and summarises it weekly with the help of AI. Before you start, here's what you need to know:"}
          </p>

          <div className="space-y-3">
            <Bullet
              icon={<Lock className="h-4 w-4 text-coral" />}
              title={ar ? "ما يبقى خاصّاً" : "What stays private"}
              body={
                ar
                  ? "كل النشاط الخام (Commits، اجتماعات، ملاحظات صوتية، إلخ) يبقى مرئياً لك أنت فقط. لا يرى أحد آخر هذه الإشارات الأوليّة."
                  : "All raw activity (commits, meetings, voice notes, calendar items) stays visible only to you. Nobody else sees these raw signals."
              }
            />
            <Bullet
              icon={<Eye className="h-4 w-4 text-coral" />}
              title={ar ? "ما يراه فريقك" : "What your team sees"}
              body={
                ar
                  ? "فقط الإيصالات التي تعتمدها (تضغط «اعتماد»). المسوّدات لا تظهر مطلقاً. يمكنك ضبط مستوى التفصيل في الإعدادات (عنوان فقط، أو كامل التفاصيل، أو المدير فقط)."
                  : "Only receipts you explicitly approve (you click \"Approve\"). Drafts are never shared. You can choose how much detail peers see in Settings (headline only, full content, or manager-only)."
              }
            />
            <Bullet
              icon={<Shield className="h-4 w-4 text-coral" />}
              title={ar ? "حقوقك" : "Your rights"}
              body={
                ar
                  ? "يمكنك تصدير بياناتك أو حذف حسابك في أيّ وقت من الإعدادات. تفاصيل كاملة في سياسة الخصوصية."
                  : "You can export your data or delete your account at any time from Settings. Full details are in our Privacy Policy."
              }
            />
          </div>

          {/* NEW TAB, not SPA <Link>: in-app navigation here changed the
              route BEHIND this consent modal (page unreadable) and then
              stranded the user on /privacy after acking. */}
          <p className="text-xs text-muted-foreground">
            {ar ? "اقرأ المزيد في " : "Read more in our "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {ar ? "سياسة الخصوصية" : "Privacy Policy"}
            </a>{" "}
            ·{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {ar ? "شروط الخدمة" : "Terms of Service"}
            </a>
            .
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <Button
            onClick={() => ack.mutate()}
            disabled={ack.isPending}
            className="bg-coral hover:bg-coral/90 text-white"
          >
            {ack.isPending
              ? "…"
              : ar
                ? "فهمت، متابعة"
                : "I understand, continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bullet({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 rounded-md bg-coral-soft p-1.5 shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{body}</div>
      </div>
    </div>
  );
}
